import { ShellNavContext } from "layouts/ResearchPreviewLayout/ShellNavContext";
import { type ReactNode, useContext, useState } from "react";
import { matchPath, useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { BuiTokenScreenLg } from "@btnet/blueprint-design-tokens";
import { IconSideDrawer } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTShellLayout, type IShellNavItem } from "@btnet/core-components/BTShellLayout";
import { BTVerticalNav } from "@btnet/core-components/BTVerticalNav";

import { type BuilderFlags } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { routes } from "utilities/routes";

import { BTLogo } from "commonComponents/btWrappers/BTLogo/BTLogo";
import {
    type IUnsavedChangesCheck,
    UnsavedChangesCheckContext,
    useUnsavedChangesCheckRegistry,
} from "commonComponents/utilities/DirtyTracking/UnsavedChangesCheckContext";
import { InfoArchOptInToggle } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/InfoArchOptInToggle";
import { useIsInfoArchOptedIn } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/useInfoArchOptIn";

interface IResearchPreviewNavItem extends IShellNavItem {
    /** The route the shell renders on for this entity. */
    path: string;
    /**
     * Whether the entity's research page is live for the builder. While it isn't, the item is
     * left out of the sidebar and its route renders without the shell, so a v1 page never ends
     * up inside it.
     */
    isEnabled: (flags: BuilderFlags | undefined) => boolean;
}

/**
 * The entities available in research preview, as the sidebar lists them. The shell renders only
 * on the `path`s of enabled items. Add one here when its list route gains a `researchComponent`.
 */
const RESEARCH_PREVIEW_NAV_ITEMS: IResearchPreviewNavItem[] = [
    {
        key: "bills",
        label: "Bills",
        group: "Financials",
        path: routes.bill.list,
        isEnabled: (flags) => flags?.billsFullPageConversion === true,
    },
];

interface INavHeaderProps {
    onCollapse: () => void;
}

const NavHeader = ({ onCollapse }: INavHeaderProps) => (
    <div className="flex items-center justify-between p-5 pb-0">
        <BTLogo width={18} />
        <BTButton
            type="tertiary"
            data-testid="nav-collapse"
            aria-label="Collapse"
            icon={<IconSideDrawer />}
            onClick={onCollapse}
        />
    </div>
);

const ShellSidebar = ({
    navItems,
    selectedKey,
    onSelectKey,
    onCollapse,
    confirmLeave,
}: {
    navItems: IShellNavItem[];
    selectedKey: string;
    onSelectKey: (key: string) => void;
    onCollapse: () => void;
    confirmLeave: IUnsavedChangesCheck["confirmLeave"];
}) => (
    <BTVerticalNav
        data-testid="research-preview-sidebar"
        navItems={navItems}
        selectedKey={selectedKey}
        onSelectKey={onSelectKey}
        top={<NavHeader onCollapse={onCollapse} />}
        bottom={
            <InfoArchOptInToggle
                label="Research preview"
                labelClassName="text-fg-secondary opacity-100"
                testIdPrefix="researchPreviewSidebarOptIn"
                confirmLeave={confirmLeave}
            />
        }
    />
);

interface IAppShellProps {
    children: ReactNode;
}

/**
 * The app's navigation shell: `BTShellLayout` and its left nav, around the app's routes. For now
 * only users opted in to research preview (info-arch) get it, and only on the routes of the
 * enabled `RESEARCH_PREVIEW_NAV_ITEMS`; everywhere else, and for everyone else, `children` render
 * unchanged.
 *
 * It sits above the routes, so the nav stays mounted across them. Pages detect it with
 * `useShellNavContext()`, not the opt-in.
 *
 * `children` keep the same parents whether or not the shell shows: off the shell, the layout is
 * `chromeless` and the context is `undefined`. Rendering `children` bare there would give them a
 * different parent, and React would remount every route whenever the shell turned on or off.
 *
 * It also holds the page's unsaved-changes check: the page registers it through
 * `UnsavedChangesCheckContext` while the shell shows, and the sidebar's Research preview switch,
 * which swaps the page's layout without navigating, asks it before switching.
 */
export const AppShell = ({ children }: IAppShellProps) => {
    const isOptedIn = useIsInfoArchOptedIn();
    const flags = useContext(BuilderInfoContext)?.flags;
    const { pathname } = useLocation();
    const belowLgBreakpoint = useMediaQuery(`(max-width: ${BuiTokenScreenLg})`);
    const [leftNavOpen, setLeftNavOpen] = useState(true);
    const unsavedChangesCheck = useUnsavedChangesCheckRegistry();

    const navItems = RESEARCH_PREVIEW_NAV_ITEMS.filter(({ isEnabled }) => isEnabled(flags));
    const selectedNavItem = isOptedIn
        ? // Not `exact`: the page's own detail URLs nest under its route (e.g. Bills puts the
          // open bill at `/Bills/Bill/:id/:jobId`), and the shell must stay up for them.
          navItems.find(({ path }) => matchPath(pathname, { path }) !== null)
        : undefined;
    // TODO: render MainNav and JobPicker when the shell is off
    const isInShell = selectedNavItem !== undefined;

    return (
        <BTShellLayout
            data-testid="research-preview-shell"
            className="research-preview-shell"
            chromeless={!isInShell}
            sidebar={
                isInShell && !belowLgBreakpoint && leftNavOpen ? (
                    <ShellSidebar
                        navItems={navItems}
                        selectedKey={selectedNavItem.key}
                        // The selected item follows the route; with one item, it's always the
                        // current page. Navigate here once there's more than one.
                        onSelectKey={() => undefined}
                        onCollapse={() => setLeftNavOpen(false)}
                        confirmLeave={unsavedChangesCheck.confirmLeave}
                    />
                ) : undefined
            }
            // No bottombar for now (ado-290557): this design has no horizontal nav,
            // only vertical. BTShellLayout keeps the slot — pass one here once there
            // are real mobile destinations.
        >
            <ShellNavContext.Provider
                value={
                    isInShell
                        ? {
                              // Below `lg` there is no sidebar to reopen (it never renders
                              // regardless of `leftNavOpen`, and there's no bottombar fallback
                              // either) — report "open" there too so a page never offers a
                              // reopen affordance with nothing behind it, e.g. narrowing below
                              // lg after collapsing on desktop.
                              isLeftNavOpen: belowLgBreakpoint || leftNavOpen,
                              openLeftNav: () => setLeftNavOpen(true),
                          }
                        : undefined
                }
            >
                <UnsavedChangesCheckContext.Provider
                    value={isInShell ? unsavedChangesCheck : undefined}
                >
                    {children}
                </UnsavedChangesCheckContext.Provider>
            </ShellNavContext.Provider>
        </BTShellLayout>
    );
};
